import { useEffect, useMemo, useRef, useState } from "react";
import {
  Typography,
  Box,
  Button,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Paper,
  Autocomplete,
  Divider,
  TableContainer,
  Chip,
} from "@mui/material";
import AdminCategories from "./adminCategories";
import { fetchUnits } from "../../Controllers/unitController";
import { fetchCategories } from "../../Controllers/categoryController";
import {
  saveSubactivity,
  deleteSubactivity,
} from "../../Controllers/subactivityController";
import { useError } from "../../Context/errorContext";

const AdminSubActivities = () => {
  const [categories, setCategories] = useState([]);
  const [subactivityOptions, setSubactivityOptions] = useState([]);

  const [units, setUnits] = useState([]);
  const [selectedSubactivity, setSelectedSubactivity] = useState(null);

  const [isEditingSub, setIsEditingSub] = useState(false);

  const [hasParentCategory, setHasParentCategory] = useState(false);
  const [unitFilter, setUnitFilter] = useState(null); // unit_id or null
  const [activityFilter, setActivityFilter] = useState(null); // activity_id or null

  const codeInputRef = useRef(null);
  const saveRefSub = useRef(null);
  const { setAlert } = useError();

  const handleSave = async () => {
    saveSubactivity(
      selectedSubactivity,
      setIsEditingSub,
      setAlert,
      setCategories,
    );
  };

  const handleDelete = async (id) => {
    deleteSubactivity(id, setAlert, setCategories);
  };

  const handleCancelSub = () => {
    if (
      !(
        selectedSubactivity.subactivity_code === null &&
        selectedSubactivity.subactivity_name === null &&
        selectedSubactivity.parent_subactivity_id === null
      )
    ) {
      const confirm = window.confirm("Cancel subactivity changes?");
      if (!confirm) return;
    }
    setIsEditingSub(false);
  };

  const codeLength = useMemo(() => {
    return (
      categories?.find(
        (c) => c.category_id === selectedSubactivity?.category_id,
      )?.code_length ?? 0
    );
  }, [categories, selectedSubactivity]);

  const categoriesByUnitActivity = useMemo(() => {
    const m = new Map();
    (categories || []).forEach((c) => {
      const key = `${c.unit_id}|${c.activity_id}`;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(c);
    });
    return m;
  }, [categories]);

  // final units to render (activities are filtered inside each unit)
  const unitsToRender = useMemo(() => {
    const base = unitFilter
      ? (units || []).filter((u) => u.unit_id === unitFilter)
      : units || [];

    const mapped = base.map((u) => ({
      ...u,
      activities: (u.activities || []).filter(
        (a) => !activityFilter || a.activity_id === activityFilter,
      ),
    }));

    // If activity filter is active, hide units that end up with 0 activities
    return activityFilter
      ? mapped.filter((u) => (u.activities || []).length > 0)
      : mapped;
  }, [units, unitFilter, activityFilter]);

  useEffect(() => {
    fetchCategories(setCategories);
    fetchUnits(setUnits);
  }, []);

  useEffect(() => {
    if (!categories) return;
    const category = categories.find(
      (c) => c.category_id === selectedSubactivity?.category_id,
    );
    const parent_category = categories.find(
      (c) => c.category_id === category?.parent_category_id,
    );

    const res = [...(parent_category?.subactivities ?? [])];
    setSubactivityOptions(res);
  }, [selectedSubactivity, categories]);

  return (
    <Paper sx={{ p: 2, mb: 4 }}>
      <AdminCategories
        fetchCategories={fetchCategories}
        setUnitFilter={setUnitFilter}
        setActivityFilter={setActivityFilter}
        setCategories={setCategories}
        categories={categories}
        units={units}
        unitFilter={unitFilter}
        activityFilter={activityFilter}
      ></AdminCategories>

      {/* Unit → Activity → Category → Subactivities */}
      <Box sx={{ display: "grid", gap: 3 }}>
        {unitsToRender.map((u) => {
          return (
            <Paper
              key={u.unit_id}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "divider",
                // Unit accent
                pl: 2.5,
                borderLeft: (t) => `6px solid ${t.palette.primary.main}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Typography variant="h5">Unit: {u.unit_name}</Typography>
                <Chip
                  size="small"
                  label={`${u.activities?.length || 0} activities`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: "grid", gap: 2 }}>
                {u.activities.map((a) => {
                  const key = `${u.unit_id}|${a.activity_id}`;
                  const cats = categoriesByUnitActivity.get(key) || [];

                  return (
                    <Paper
                      key={a.activity_id}
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        // Activity accent
                        pl: 2,
                        borderLeft: (t) =>
                          `4px solid ${t.palette.secondary.main}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                          flexWrap: "wrap",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6">
                          Activity: {a.activity_name}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${cats.length} categories`}
                          color="secondary"
                          variant="outlined"
                        />
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      <Box sx={{ display: "grid", gap: 2 }}>
                        {cats.map((c) => (
                          <Paper
                            key={c.category_id}
                            variant="outlined"
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              // Category accent
                              pl: 2,
                              borderLeft: (t) =>
                                `3px solid ${t.palette.info.main}`,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 2,
                                flexWrap: "wrap",
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Typography variant="h6" fontWeight={600}>
                                  Category: {c.category_name}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={`${
                                    c.subactivities?.length || 0
                                  } subactivities`}
                                  color="info"
                                  variant="outlined"
                                />
                              </Box>

                              <Button
                                variant="contained"
                                onClick={() => {
                                  setSelectedSubactivity({
                                    subactivity_code: null,
                                    subactivity_name: null,
                                    category_id: c.category_id,
                                    parent_subactivity_id: null,
                                  });
                                  setIsEditingSub(true);
                                  setHasParentCategory(
                                    c.parent_category_id !== null,
                                  );
                                }}
                              >
                                Add Subactivity
                              </Button>
                            </Box>

                            <Divider sx={{ mb: 1 }} />

                            <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
                              <Typography
                                variant="subtitle2"
                                sx={{ mb: 1 }}
                                color="text.secondary"
                              >
                                Subactivities
                              </Typography>

                              {/* Scrollable + sticky header + zebra rows */}
                              <TableContainer
                                sx={{
                                  maxHeight: 360,
                                  borderRadius: 2,
                                  border: 1,
                                  borderColor: "divider",
                                }}
                              >
                                <Table size="small" stickyHeader>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Code</TableCell>
                                      <TableCell>Name</TableCell>
                                      <TableCell>Parent Sub</TableCell>
                                      <TableCell align="right">
                                        Actions
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody
                                    sx={{
                                      "& tr:nth-of-type(odd)": {
                                        bgcolor: (t) =>
                                          t.palette.mode === "dark"
                                            ? t.palette.action.hover
                                            : t.palette.action.hover,
                                      },
                                    }}
                                  >
                                    {c.subactivities.map((sub) => (
                                      <TableRow key={sub.subactivity_id} hover>
                                        <TableCell>
                                          {sub.subactivity_code}
                                        </TableCell>
                                        <TableCell>
                                          {sub.subactivity_name}
                                        </TableCell>
                                        <TableCell>
                                          {sub.Parent?.subactivity_name || "—"}
                                        </TableCell>
                                        <TableCell align="right">
                                          <Box
                                            sx={{
                                              display: "inline-flex",
                                              gap: 1,
                                            }}
                                          >
                                            <Button
                                              variant="contained"
                                              onClick={() => {
                                                setSelectedSubactivity(sub);
                                                setIsEditingSub(true);
                                                setHasParentCategory(
                                                  c.parent_category_id !== null,
                                                );
                                              }}
                                              size="small"
                                            >
                                              Edit
                                            </Button>
                                            <Button
                                              variant="contained"
                                              color="error"
                                              onClick={() =>
                                                handleDelete(sub.subactivity_id)
                                              }
                                              size="small"
                                            >
                                              Delete
                                            </Button>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>

                            {/* <Box sx={{ mt: 2 }}>
                              <AdminCsvUpload
                                refresh={fetchCategories}
                                csvType={"subactivity"}
                                subactivities={c.subactivities}
                              />
                            </Box> */}
                          </Paper>
                        ))}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Subactivity dialog */}
      <Dialog
        open={isEditingSub}
        onClose={() => {
          setIsEditingSub(false);
          setHasParentCategory(false);
        }}
        fullWidth
      >
        <DialogTitle>
          {selectedSubactivity?.subactivity_id
            ? "Edit Subactivity"
            : "Add Subactivity"}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Code"
            value={selectedSubactivity?.subactivity_code || ""}
            onChange={(e) =>
              setSelectedSubactivity({
                ...selectedSubactivity,
                subactivity_code: e.target.value,
              })
            }
            inputProps={{
              minLength: codeLength,
              maxLength: codeLength,
            }}
            margin="normal"
            helperText={`Code length: ${codeLength}`}
            required
            inputRef={codeInputRef}
          />

          <TextField
            fullWidth
            label="Name"
            value={selectedSubactivity?.subactivity_name || ""}
            onChange={(e) =>
              setSelectedSubactivity({
                ...selectedSubactivity,
                subactivity_name: e.target.value,
              })
            }
            onKeyDown={(e) => {
              if (!hasParentCategory && e.key === "Tab") {
                setTimeout(() => {
                  saveRefSub.current?.focus();
                }, 0);
              }
            }}
            margin="normal"
          />

          {hasParentCategory && (
            <Autocomplete
              options={subactivityOptions}
              getOptionLabel={(option) => option.subactivity_name}
              isOptionEqualToValue={(option, value) =>
                option.subactivity_id === value.parent_subactivity_id
              }
              value={
                subactivityOptions.find(
                  (a) =>
                    a.subactivity_id ===
                    selectedSubactivity?.parent_subactivity_id,
                ) || null
              }
              onChange={(e, newValue) =>
                setSelectedSubactivity({
                  ...selectedSubactivity,
                  parent_subactivity_id: newValue
                    ? newValue.subactivity_id
                    : null,
                })
              }
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  setTimeout(() => {
                    saveRefSub.current?.focus();
                  }, 0);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Subactivity"
                  fullWidth
                  margin="normal"
                />
              )}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleCancelSub}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const el = codeInputRef.current;
              if (!el.checkValidity()) {
                el.reportValidity(); // shows the native tooltip
                return; // stop saving
              }
              handleSave();
            }}
            ref={saveRefSub}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminSubActivities;
